import { rdbV1FiltersYearsList } from '@on3/api';
import type { ITeamRosterProps } from '@on3/ui-lib/src/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { capitalizeFirstLetter } from '@on3/ui-lib/utils/capitalizeFirstLetter';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { TeamRosterList } from 'components/College/TeamRosterList';
import { DatabaseLayout } from 'components/Layouts/DatabaseLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';

const TeamRosterIndex = (props: ITeamRosterProps) => {
  const { currentSite } = props?.siteData || {};
  const router = useRouter();
  const { year, sport } = router.query;
  const capitilzedSport = capitalizeFirstLetter(`${sport}`);
  const orgName =
    currentSite?.organization?.knownAs ?? currentSite?.organization?.name;
  const orgMascot = currentSite?.organization?.mascot;
  const title = `${year} ${orgName} ${orgMascot} ${capitilzedSport} Roster`;
  const description = `${year} ${orgName} ${orgMascot} ${capitilzedSport} team roster.`;
  const noIndex = props?.rosterList?.pagination?.count === 0;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <DatabaseLayout>
        <TeamRosterList
          {...props}
          key={`${year}-${sport}-${orgName}`}
          title={title}
        />
      </DatabaseLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  // move this to a util
  const orgKey = siteData?.currentSite?.organizationKey;
  const { year, sport } = ctx.query;

  // validation checks
  // TODO: Validate year is in range.

  const { sportsData } = siteData || {};
  const sportKey = sportsData?.find(
    (sp) => sp?.name && slugify(sp?.name) === sport,
  )?.key;

  if (!orgKey || !year || !sport || !sportKey) {
    return {
      notFound: true,
    };
  }

  const params = {
    sportKey,
    year,
    organizationKey: orgKey,
  };

  try {
    const [rosterList, rosterHeader, teams, years] = await Promise.allSettled([
      proxyApi
        .get(`/public/rdb/v1/organizations/${orgKey}/roster`, {
          params,
        })
        .then((r) => r.data),
      proxyApi
        .get(`/public/rdb/v1/organizations/${orgKey}/roster-header`, {
          params,
        })
        .then((r) => r.data),

      proxyApi
        .get(
          `/public/rdb/v1/filters/teams?groupBy=true&sportKey=${sportKey}&year=${year}`,
        )
        .then((r) => r.data),
      rdbV1FiltersYearsList(proxyApi),
    ]);

    const data = {
      pageType: 'database',
      siteData: siteData || [],
      userData: userData || {},
      rosterList: getFulfilledValue(rosterList),
      rosterHeader: getFulfilledValue(rosterHeader),
      teams: getFulfilledValue(teams),
      years: getFulfilledValue(years),
      sports: sportsData,
    };

    return { props: { ...data } };
  } catch (e) {
    console.log(e);

    return { props: [] };
  }
};

export default withValidUrl(TeamRosterIndex);
